import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Options } from 'components/Options/Options'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import useQueryPagination from 'hooks/useQueryPagination'
import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { receiptsOptions } from './receiptsData'
import { approveInventoryReceipt, clearReceipts, getInventoryReceipts, rejectInventoryReceipt } from 'features/inventory/inventoryReceiptsSlice'
import { useNavigate } from 'react-router-dom'
import { setConfirmModal } from 'features/global/globalSlice'
import { FilterEnum } from 'components/Filter/SingleFilter'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { IInventoryDemoReceiptRow } from 'types/inventorySupply.interface'
import { INVENTORY_DEMO_RECEIPTS, INVENTORY_RECEIPT_QC_CAPTION, RECEIPT_QC_META } from '../inventorySupplyDemoData'
import { ReceiptQcModal } from './ReceiptQcModal'

const getReceiptStatusTone = (status: number): IStatusPill['status'] => {
    switch (status) {
        case 2:
            return 'success'
        case 1:
            return 'info'
        case 3:
            return 'neutral'
        default:
            return 'neutral'
    }
}

export const InventoryReceipts = () => {
    const { receipts, totalPages } = useSelector((store: RootState) => store.inventoryReceipts)
    const [params, setParams] = useState<any>(getParams({ location }))
    const [qcModalReceipt, setQcModalReceipt] = useState<IInventoryDemoReceiptRow | null>(null)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    useQueryPagination({ fetchFunctions: [getInventoryReceipts], cleanUpFunctions: [clearReceipts], params })

    const usingDemoReceipts = !receipts?.length && INVENTORY_DEMO_RECEIPTS.length > 0

    const rows: any[] = useMemo(
        () => (receipts?.length ? receipts : INVENTORY_DEMO_RECEIPTS),
        [receipts],
    )

    const approveReceipt = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(approveInventoryReceipt(id))
                },
                word: 'approve this receipt',
            }),
        )
    }

    const rejectReceipt = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(rejectInventoryReceipt(id))
                },
                word: 'reject this receipt',
            }),
        )
    }

    const filters = [
        {
            type: FilterEnum.SELECT,
            name: 'Status',
            options: receiptsOptions,
            propName: 'status'
        },
    ]

    const columns: IHonestTableColumn<any>[] = [
        { id: 'supplier', header: 'SUPPLIER', cell: (item) => item.supplier?.name || '/' },
        { id: 'location', header: 'LOCATION', cell: (item) => item.location?.name || '/' },
        { id: 'receiptNo', header: 'RECEIPT NUMBER', cell: (item) => item.receiptNo || '/' },
        { id: 'receiptDate', header: 'RECEIPT DATE', cell: (item) => formatDateForDisplay(item.receiptDate) },
        {
            id: 'status',
            header: 'RECEIPT STATUS',
            cell: (item) => (
                <StatusPill status={getReceiptStatusTone(item.status)}>
                    {receiptsOptions.find(o => o.value == item.status)?.label}
                </StatusPill>
            ),
        },
        { id: 'currency', header: 'RECEIPT CURRENCY', cell: (item) => item.currency || '/' },
        { id: 'amount', header: 'AMOUNT', cell: (item) => item.totalAmount || '/' },
        {
            id: 'qc',
            header: 'QC',
            cell: (item) => (item.qc ? (
                <StatusPill status={RECEIPT_QC_META[item.qc.result].tone} dot={item.qc.result === 'REJECTED'}>
                    {RECEIPT_QC_META[item.qc.result].label}
                </StatusPill>
            ) : (
                '—'
            )),
        },
        {
            id: 'actions',
            header: '',
            align: 'right',
            cell: (item) => (usingDemoReceipts ? null : (
                <span onClick={(e) => e.stopPropagation()}>
                    <Options openModal={(approve: boolean) => approve ? approveReceipt(item.id) : rejectReceipt(item.id)} item={item} title='Receipts' />
                </span>
            )),
        },
    ]

    return <>
        <NavBar enableTemporaryManualClosing input setParams={setParams} placeholder='Search receipts...' filterOptions={filters} visibleFilterOverflow />
        <PageWrapper>
            <Flex between center mb='16' >
                <Flex column gap='0.25rem'>
                    <Flex center gap='0.75rem'>
                        <MainHeadline>Manual Receipts</MainHeadline>
                        {usingDemoReceipts && <DemoBadge />}
                    </Flex>
                    <OtherText grey>Goods received without a purchase order — PO deliveries land under Receiving automatically</OtherText>
                </Flex>
                <Flex gap='1rem' >
                    <Button variant='primary' onClick={() => navigate('add')} >
                        Add Receipt
                    </Button>
                </Flex>

            </Flex>
            {!rows.length ? (
                <NoItems title='Receipts' />
            ) : (
                <>
                    <HonestTable
                        columns={columns}
                        data={rows}
                        keyField='id'
                        onClick={(item) => (item.isDemo ? setQcModalReceipt(item) : navigate(item.id.toString()))}
                    />
                    {usingDemoReceipts && (
                        <Flex mt='16'>
                            <OtherText color={tokens.text.muted}>{INVENTORY_RECEIPT_QC_CAPTION}</OtherText>
                        </Flex>
                    )}
                </>
            )}
            {qcModalReceipt && (
                <ReceiptQcModal receipt={qcModalReceipt} closeModal={() => setQcModalReceipt(null)} />
            )}
        </PageWrapper>
        <PageFooter>
            <Pagination
                setPage={(page) => handlePagination(page, setParams, 30)}
                pageCount={Math.ceil(totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
            />
        </PageFooter>
    </>
}